import type { MouseEvent } from 'react';
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  CogIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import type { NotificationDTO } from '@/features/notifications/api';

interface NotificationItemProps {
  notification: NotificationDTO;
  onMarkRead: (id: string) => void;
  isMarkingRead?: boolean;
  onClose?: () => void;
}

function getNotificationIcon(type: string) {
  const iconClass = 'h-5 w-5 text-gray-400';

  switch (type.toUpperCase()) {
    case 'LEAD':
      return <UserGroupIcon className={iconClass} />;
    case 'TASK':
      return <ClipboardDocumentListIcon className={iconClass} />;
    case 'APPOINTMENT':
      return <CalendarIcon className={iconClass} />;
    case 'SYSTEM':
      return <CogIcon className={iconClass} />;
    default:
      return <ExclamationTriangleIcon className={iconClass} />;
  }
}

function getTypeBadgeClass(type: string): string {
  switch (type.toUpperCase()) {
    case 'LEAD':
      return 'bg-green-100 text-green-800';
    case 'TASK':
      return 'bg-blue-100 text-blue-800';
    case 'APPOINTMENT':
      return 'bg-purple-100 text-purple-800';
    case 'SYSTEM':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
}

/** Resolves a click-through destination from the notification's free-form
 * `data` payload — the real API has no `clickAction`/typed nav fields, so
 * this only navigates when the data explicitly carries one of these ids. */
function resolveClickTarget(notification: NotificationDTO): string | null {
  const data = notification.data;
  if (!data) return null;

  switch (notification.type.toUpperCase()) {
    case 'LEAD':
      return typeof data.leadId === 'string' ? `/leads/${data.leadId}` : null;
    case 'TASK':
      return typeof data.taskId === 'string' ? `/tasks/${data.taskId}` : null;
    case 'APPOINTMENT':
      return typeof data.appointmentId === 'string' ? `/appointments/${data.appointmentId}` : null;
    default:
      return null;
  }
}

export function NotificationItem({ notification, onMarkRead, isMarkingRead, onClose }: NotificationItemProps) {
  const handleClick = () => {
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }

    const target = resolveClickTarget(notification);
    if (target) {
      window.location.href = target;
    }

    onClose?.();
  };

  const handleMarkReadClick = (event: MouseEvent) => {
    event.stopPropagation();
    onMarkRead(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      data-testid="notification-item"
      data-notification-id={notification.id}
      data-read={notification.is_read}
      className={`
        p-4 hover:bg-gray-50 cursor-pointer transition-colors border-l-4 border-gray-200
        ${!notification.is_read ? 'bg-blue-25' : 'bg-white'}
      `}
    >
      <div className="flex items-start space-x-3">
        {/* Notification Icon */}
        <div className="flex-shrink-0 mt-0.5">
          <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
            {getNotificationIcon(notification.type)}
          </div>
        </div>

        {/* Notification Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                {notification.title}
              </p>
              {notification.body && (
                <p className={`text-sm ${!notification.is_read ? 'text-gray-700' : 'text-gray-500'} mt-1`}>
                  {notification.body}
                </p>
              )}

              {/* Metadata */}
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                </span>

                {/* Type Badge */}
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeClass(notification.type)}`}
                >
                  {notification.type.toLowerCase()}
                </span>
              </div>
            </div>

            {/* Unread Indicator + Mark Read */}
            {!notification.is_read && (
              <div className="flex-shrink-0 ml-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleMarkReadClick}
                  disabled={isMarkingRead}
                  data-testid="notification-mark-read"
                  title="Mark as read"
                  className="p-1 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
